import Decimal from 'decimal.js';
import { btcUsdPerUnitOnUtcDayNearest, ensureBtcUsdDailyLoaded } from './btcUsdDaily';
import { ethUsdPerUnitOnUtcDayNearest, ensureEthUsdDailyLoaded } from './ethUsdDaily';
import { croUsdPerUnitOnUtcDayNearest, ensureCroUsdDailyLoaded } from './croUsdDaily';
import { convertUsdToEur } from './eurFx';

/**
 * EUR per 1 CRO — bundled daily **USD** (`cro-usd-max.csv`) × Frankfurter USD→EUR for that UTC day.
 * `blockHint` is ignored (series is date-based).
 */
export async function nativeCroEurPerUnit(
  date: Date,
  _blockHint?: number | null
): Promise<Decimal | null> {
  void _blockHint;
  const usd = croUsdPerUnitOnUtcDayNearest(date);
  if (usd == null) return null;
  return convertUsdToEur(new Decimal(usd), date);
}

/**
 * EUR per 1 WBTC — bundled daily BTC/USD (`btc-usd-max.csv`) × Frankfurter USD→EUR (WBTC ≈ 1:1 BTC).
 */
export async function wrappedBtcEurPerUnit(
  date: Date,
  _blockHint?: number | null
): Promise<Decimal | null> {
  void _blockHint;
  const usd = btcUsdPerUnitOnUtcDayNearest(date);
  if (usd == null) return null;
  return convertUsdToEur(new Decimal(usd), date);
}

/**
 * EUR per 1 ETH — proxy ETH/USD from bundled BTC/USD (see `ethUsdDaily.ts`) × Frankfurter USD→EUR.
 */
export async function nativeEthEurPerUnit(
  date: Date,
  _blockHint?: number | null
): Promise<Decimal | null> {
  void _blockHint;
  const usd = ethUsdPerUnitOnUtcDayNearest(date);
  if (usd == null) return null;
  return convertUsdToEur(new Decimal(usd), date);
}

/**
 * Ensures the bundled CRO/USD table is parsed (in-memory). No network calls.
 * `minDelayMsBetweenNetworkCalls` is accepted for API compatibility and ignored.
 */
export async function prefetchCroHistoricDailyRates(
  _dates: Date[],
  options?: {
    minDelayMsBetweenNetworkCalls?: number;
    onProgress?: (msg: string) => void;
  }
): Promise<void> {
  options?.onProgress?.('CRO/EUR: loading bundled daily CRO/USD (cro-usd-max.csv)…');
  ensureCroUsdDailyLoaded();
  options?.onProgress?.('BTC/USD: loading bundled daily BTC/USD (btc-usd-max.csv)…');
  ensureBtcUsdDailyLoaded();
  options?.onProgress?.('ETH/USD: proxy from BTC daily (ethUsdDaily.ts)…');
  ensureEthUsdDailyLoaded();
}

/**
 * Arbitrary CRC-20 (non-WCRO) EUR/unit — not fetched from external APIs.
 * Token fiat values are derived from CRO swap ratios + historical CRO/EUR in `applyCronosHistoricPrices`.
 */
export async function crc20EurOnDate(
  _contractAddress: string,
  _date: Date,
  _blockHint?: number | null
): Promise<Decimal | null> {
  return null;
}
